/**
 * 用户行为追踪模块（埋点系统）
 * 用于追踪用户点击、页面访问、接口调用等行为
 */
import { addBreadcrumb, setContext } from './sentry';
import { sanitizeData } from './sentry.helper';

// 批量上报队列
let breadcrumbQueue = [];
const BATCH_SIZE = 10;  // 批量上报数量
const BATCH_TIMEOUT = 5000;  // 批量上报超时时间（毫秒）
let batchTimer = null;

/**
 * 批量上报面包屑
 */
const flushBreadcrumbs = () => {
  if (breadcrumbQueue.length === 0) return;
  
  const queue = [...breadcrumbQueue];
  breadcrumbQueue = [];
  
  queue.forEach(breadcrumb => {
    addBreadcrumb(breadcrumb);
  });
  
  console.log(`[Tracker] Flushed ${queue.length} breadcrumbs`);
};

/**
 * 添加面包屑到队列
 * @param {Object} breadcrumb - 面包屑数据
 */
const queueBreadcrumb = (breadcrumb) => {
  breadcrumbQueue.push(breadcrumb);
  
  // 达到批量上报数量时立即上报
  if (breadcrumbQueue.length >= BATCH_SIZE) {
    clearTimeout(batchTimer);
    flushBreadcrumbs();
  } else {
    // 重置定时器
    clearTimeout(batchTimer);
    batchTimer = setTimeout(flushBreadcrumbs, BATCH_TIMEOUT);
  }
};

/**
 * 追踪页面访问
 * @param {String} pageName - 页面名称
 * @param {Object} params - 页面参数
 */
export const trackPageView = (pageName, params = {}) => {
  try {
    const sanitizedParams = sanitizeData(params);
    
    queueBreadcrumb({
      category: 'navigation',
      message: `Page View: ${pageName}`,
      level: 'info',
      data: {
        page: pageName,
        params: sanitizedParams,
        timestamp: Date.now(),
      },
    });
    
    // 设置页面上下文
    setContext('page', {
      name: pageName,
      params: sanitizedParams,
      url: window.location.href,
      referrer: document.referrer,
    });
  } catch (error) {
    console.error('[Tracker] Failed to track page view:', error);
  }
};

/**
 * 追踪按钮点击
 * @param {String} buttonName - 按钮名称/标识
 * @param {Object} data - 额外数据
 */
export const trackButtonClick = (buttonName, data = {}) => {
  try {
    const sanitizedData = sanitizeData(data);
    
    queueBreadcrumb({
      category: 'user',
      message: `Button Click: ${buttonName}`,
      level: 'info',
      data: {
        button: buttonName,
        ...sanitizedData,
        timestamp: Date.now(),
      },
    });
  } catch (error) {
    console.error('[Tracker] Failed to track button click:', error);
  }
};

/**
 * 追踪表单提交
 * @param {String} formName - 表单名称
 * @param {Object} formData - 表单数据（会自动清理敏感信息）
 */
export const trackFormSubmit = (formName, formData = {}) => {
  try {
    const sanitizedData = sanitizeData(formData);
    
    queueBreadcrumb({
      category: 'user',
      message: `Form Submit: ${formName}`,
      level: 'info',
      data: {
        form: formName,
        data: sanitizedData,
        timestamp: Date.now(),
      },
    });
  } catch (error) {
    console.error('[Tracker] Failed to track form submit:', error);
  }
};

/**
 * 追踪 API 请求
 * @param {Object} requestInfo - 请求信息
 */
export const trackApiRequest = (requestInfo) => {
  try {
    const { url, method, data, requestId } = requestInfo;
    const sanitizedData = sanitizeData(data);
    
    queueBreadcrumb({
      category: 'http',
      type: 'http',
      message: `API Request: ${method} ${url}`,
      level: 'info',
      data: {
        url,
        method,
        data: sanitizedData,
        requestId,
        timestamp: Date.now(),
      },
    });
  } catch (error) {
    console.error('[Tracker] Failed to track API request:', error);
  }
};

/**
 * 追踪 API 响应
 * @param {Object} responseInfo - 响应信息
 */
export const trackApiResponse = (responseInfo) => {
  try {
    const { url, method, statusCode, duration, success, requestId, error } = responseInfo;
    
    queueBreadcrumb({
      category: 'http',
      type: 'http',
      message: `API Response: ${method} ${url} - ${statusCode}`,
      level: success ? 'info' : 'error',
      data: {
        url,
        method,
        statusCode,
        duration,
        success,
        requestId,
        error: error ? sanitizeData({ message: error.message, stack: error.stack }) : null,
        timestamp: Date.now(),
      },
    });
  } catch (error) {
    console.error('[Tracker] Failed to track API response:', error);
  }
};

/**
 * 追踪 API 错误
 * @param {Object} errorInfo - 错误信息
 */
export const trackApiError = (errorInfo) => {
  try {
    const { url, method, statusCode, error, data, requestId } = errorInfo;
    const sanitizedData = sanitizeData(data);
    
    queueBreadcrumb({
      category: 'http',
      type: 'http',
      message: `API Error: ${method} ${url}`,
      level: 'error',
      data: {
        url,
        method,
        statusCode,
        requestData: sanitizedData,
        error: error ? {
          message: error.message,
          code: error.code,
        } : null,
        requestId,
        timestamp: Date.now(),
      },
    });
    
    // 设置错误上下文
    setContext('api_error', {
      url,
      method,
      statusCode,
      requestData: sanitizedData,
      requestId,
    });
  } catch (error) {
    console.error('[Tracker] Failed to track API error:', error);
  }
};

/**
 * 追踪用户操作
 * @param {String} action - 操作名称
 * @param {Object} data - 操作数据
 */
export const trackUserAction = (action, data = {}) => {
  try {
    const sanitizedData = sanitizeData(data);
    
    queueBreadcrumb({
      category: 'user',
      message: `User Action: ${action}`,
      level: 'info',
      data: {
        action,
        ...sanitizedData,
        timestamp: Date.now(),
      },
    });
  } catch (error) {
    console.error('[Tracker] Failed to track user action:', error);
  }
};

/**
 * 追踪错误
 * @param {String} errorType - 错误类型
 * @param {Error} error - 错误对象
 * @param {Object} context - 上下文信息
 */
export const trackError = (errorType, error, context = {}) => {
  try {
    const sanitizedContext = sanitizeData(context);
    
    // 立即上报，不加入批量队列
    addBreadcrumb({
      category: 'error',
      message: `Error: ${errorType}`,
      level: 'error',
      data: {
        type: errorType,
        message: error.message,
        stack: error.stack,
        context: sanitizedContext,
        timestamp: Date.now(),
      },
    });
  } catch (e) {
    console.error('[Tracker] Failed to track error:', e);
  }
};

/**
 * 追踪性能指标
 * @param {String} metricName - 指标名称
 * @param {Number} value - 指标值
 * @param {Object} data - 额外数据
 */
export const trackPerformance = (metricName, value, data = {}) => {
  try {
    queueBreadcrumb({
      category: 'performance',
      message: `Performance: ${metricName}`,
      level: 'info',
      data: {
        metric: metricName,
        value,
        ...data,
        timestamp: Date.now(),
      },
    });
  } catch (error) {
    console.error('[Tracker] Failed to track performance:', error);
  }
};

/**
 * 立即刷新所有待上报的数据
 * 通常在页面卸载时调用
 */
export const flushTracking = () => {
  clearTimeout(batchTimer);
  flushBreadcrumbs();
};

// 页面卸载时自动刷新
if (typeof window !== 'undefined') {
  window.addEventListener('beforeunload', flushTracking);
}

export default {
  trackPageView,
  trackButtonClick,
  trackFormSubmit,
  trackApiRequest,
  trackApiResponse,
  trackApiError,
  trackUserAction,
  trackError,
  trackPerformance,
  flushTracking,
};

